import React, { useMemo, useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, Modal, SummaryList, useAsync, useSession, useToast } from '../ui.jsx';
import { auditLabel, centsToInput, dateLabel, money, monthLabel, nameOf, parseAmountToCents, timestampLabel, unitName } from '../lib/format.js';
import { Link } from '../main.jsx';

/**
 * 歷史補錄 — a separate entry point for facts from before the 帳簿啟用日. It produces
 * no notification, task or overdue behaviour, and it never claims a payment was
 * submitted or confirmed inside the App.
 */
export default function HistoricalPage() {
  const session = useSession();
  const records = useAsync(() => api.historicalRecords(), []);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  if (records.loading) return <Loading />;
  if (records.error) return <ErrorBanner error={records.error} />;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">帳簿控制台 · 獨立入口</div>
          <h1>歷史補錄</h1>
          <p className="small muted">
            只接受帳簿啟用日 <strong data-testid="activation-date">{records.data.activation_date}</strong> 之前已結清或已取消的舊帳。
            仍需追收或啟用日後才完成付款的舊帳，請改用正常付款流程。
          </p>
        </div>
        <button type="button" className="primary" onClick={() => setEditing({ mode: 'create' })} data-testid="historical-new">
          新增歷史補錄
        </button>
      </div>

      <div className="banner info small">
        歷史補錄不會產生通知、待辦或逾期提醒，也不要求成員重新提交；它只聲明補錄者及補錄時間，不冒充曾在 App 內提交或確認。
      </div>

      <Card title="已補錄的歷史帳目" testId="historical-list">
        {records.data.items.length === 0 ? <p className="small muted" data-testid="historical-empty">尚未有歷史補錄。</p> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>付款項目</th><th>單位</th><th>帳目日期</th><th className="num">總額</th>
                  <th>結果</th><th className="num">歷史付款合計</th><th>補錄者</th><th />
                </tr>
              </thead>
              <tbody>
                {records.data.items.map((item) => {
                  const paid = item.dues.reduce((total, due) => total
                    + due.historical_payments.reduce((sum, payment) => sum + payment.amount_cents, 0), 0);
                  return (
                    <tr key={item.id} data-testid={`historical-item-${item.id}`}>
                      <td><Link to={`/items/${item.id}`}>{item.title}</Link><div className="small muted">{item.category} · {monthLabel(item.accounting_month)}</div></td>
                      <td>{unitName(session.all_units, item.unit_id)}</td>
                      <td>{dateLabel(item.issue_date)}</td>
                      <td className="num">{money(item.total_cents)}</td>
                      <td>
                        <span className={`chip ${item.historical_outcome === 'settled' ? 'settled' : 'cancelled'}`}>
                          {item.historical_outcome === 'settled' ? '已結清' : '已取消'}
                        </span>
                      </td>
                      <td className="num" data-testid={`historical-paid-${item.id}`}>{money(paid)}</td>
                      <td className="small">
                        {nameOf(session.members, item.recorded_by)}
                        <div className="muted">{timestampLabel(item.recorded_at)}</div>
                      </td>
                      <td>
                        <div className="row">
                          <button type="button" className="small" onClick={() => setEditing({ mode: 'correct', item })} data-testid={`historical-correct-${item.id}`}>修正</button>
                          <button type="button" className="small" onClick={() => setHistoryFor(item)} data-testid={`historical-history-${item.id}`}>留痕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing ? (
        <HistoricalForm
          mode={editing.mode}
          item={editing.item}
          activationDate={records.data.activation_date}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); records.reload(); }}
        />
      ) : null}

      {historyFor ? <HistoricalHistory item={historyFor} onClose={() => setHistoryFor(null)} /> : null}
    </div>
  );
}

function HistoricalHistory({ item, onClose }) {
  const session = useSession();
  const history = useAsync(() => api.historicalHistory(item.id), [item.id]);
  return (
    <Modal title={`留痕：${item.title}`} subtitle="帳簿顯示最新有效結果，所有先前版本仍可查看" onClose={onClose} wide>
      {history.loading ? <Loading /> : null}
      {history.error ? <ErrorBanner error={history.error} /> : null}
      {history.data ? (
        <>
          <ul className="audit-list">
            {history.data.events.map((event) => (
              <li key={event.id} data-testid={`historical-audit-${event.action}`}>
                <strong>{auditLabel(event.action)}</strong> · {nameOf(session.members, event.actor_member_id)} · {timestampLabel(event.created_at)}
                {event.reason ? <div>原因：{event.reason}</div> : null}
                {event.before_json ? <pre className="json">前：{event.before_json}</pre> : null}
                {event.after_json ? <pre className="json">後：{event.after_json}</pre> : null}
              </li>
            ))}
          </ul>
          {history.data.superseded_payments.length ? (
            <div className="small" style={{ marginTop: 12 }} data-testid="superseded-payments">
              <strong>已被取代的歷史付款（保留不刪除）</strong>
              <ul className="audit-list">
                {history.data.superseded_payments.map((payment) => (
                  <li key={payment.id}>
                    {payment.reference} · {nameOf(session.members, payment.payer_member_id)} · {money(payment.amount_cents)} ·
                    付款日期 {dateLabel(payment.paid_on)} · 取代於 {timestampLabel(payment.superseded_at)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

function HistoricalForm({ mode, item, activationDate, onClose, onDone }) {
  const session = useSession();
  const notify = useToast();
  const members = session.members.filter((member) => member.status === 'active');
  const beforeActivation = new Date(new Date(activationDate).getTime() - 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState(() => (mode === 'correct' ? {
    unit_id: item.unit_id,
    title: item.title,
    category: item.category,
    accounting_month: item.accounting_month,
    item_date: item.issue_date,
    due_date: item.due_date,
    total: centsToInput(item.total_cents),
    outcome: item.historical_outcome,
    recipient_kind: item.recipient_kind,
    recipient_member_id: item.recipient_member_id || members[0].id,
    recipient_name: item.recipient_name || '',
    cancel_reason: item.cancel_reason || '',
    cancelled_on: item.cancelled_on || beforeActivation,
    reason: '',
  } : {
    unit_id: session.all_units[0].id,
    title: '',
    category: '租金',
    accounting_month: beforeActivation.slice(0, 7),
    item_date: beforeActivation,
    due_date: beforeActivation,
    total: '',
    outcome: 'settled',
    recipient_kind: 'external',
    recipient_member_id: members[0].id,
    recipient_name: '',
    cancel_reason: '',
    cancelled_on: beforeActivation,
    reason: '',
  }));

  const [allocations, setAllocations] = useState(() => (mode === 'correct'
    ? item.dues.filter((due) => due.amount_cents > 0).map((due) => ({ member_id: due.member_id, amount: centsToInput(due.amount_cents) }))
    : members.map((member) => ({ member_id: member.id, amount: '' }))));

  const [payments, setPayments] = useState(() => (mode === 'correct'
    ? item.dues.flatMap((due) => due.historical_payments.map((payment) => ({
      payer_member_id: payment.payer_member_id,
      amount: centsToInput(payment.amount_cents),
      paid_on: payment.paid_on,
      note: payment.note || '',
      proof_ids: payment.proof_ids || [],
    })))
    : []));

  const [dispositions, setDispositions] = useState(() => (mode === 'correct'
    ? item.dues.flatMap((due) => due.balances.flatMap((balance) => balance.dispositions.map((disposition) => ({
      member_id: due.member_id,
      kind: disposition.kind,
      amount: centsToInput(disposition.amount_cents),
      handled_on: disposition.handled_on,
      reason: disposition.reason || '',
    }))))
    : []));

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const totalCents = parseAmountToCents(form.total);
  const allocationSum = allocations.reduce((total, allocation) => total + (parseAmountToCents(allocation.amount) || 0), 0);
  const paidByMember = useMemo(() => {
    const map = new Map();
    for (const payment of payments) {
      map.set(payment.payer_member_id, (map.get(payment.payer_member_id) || 0) + (parseAmountToCents(payment.amount) || 0));
    }
    return map;
  }, [payments]);

  const payload = () => ({
    unit_id: form.unit_id,
    title: form.title.trim(),
    category: form.category,
    accounting_month: form.accounting_month,
    item_date: form.item_date,
    due_date: form.due_date,
    total_cents: totalCents,
    outcome: form.outcome,
    recipient: form.recipient_kind === 'member'
      ? { kind: 'member', member_id: form.recipient_member_id }
      : { kind: 'external', name: form.recipient_name.trim() },
    allocations: allocations
      .filter((allocation) => parseAmountToCents(allocation.amount))
      .map((allocation) => ({ member_id: allocation.member_id, amount_cents: parseAmountToCents(allocation.amount) })),
    payments: payments.map((payment) => ({
      payer_member_id: payment.payer_member_id,
      amount_cents: parseAmountToCents(payment.amount),
      paid_on: payment.paid_on,
      note: payment.note.trim() || undefined,
      proof_ids: payment.proof_ids,
    })),
    cancellation: form.outcome === 'cancelled' ? {
      reason: form.cancel_reason.trim(),
      cancelled_on: form.cancelled_on,
      dispositions: dispositions.map((disposition) => ({
        member_id: disposition.member_id,
        kind: disposition.kind,
        amount_cents: parseAmountToCents(disposition.amount),
        handled_on: disposition.handled_on,
        reason: disposition.reason.trim() || undefined,
      })),
    } : undefined,
  });

  const runPreview = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await api.previewHistorical(payload());
      setPreview(result.summary);
    } catch (failure) {
      setError(failure);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setError(null);
    setBusy(true);
    try {
      if (mode === 'correct') {
        await api.correctHistorical(item.id, { ...payload(), confirm: true, reason: form.reason.trim() });
        notify('歷史補錄已修正，原始事實仍完整保留');
      } else {
        await api.createHistorical({ ...payload(), confirm: true });
        notify('歷史補錄已儲存');
      }
      onDone();
    } catch (failure) {
      setError(failure);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const uploadProof = async (index, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.set('image', file);
      const result = await api.uploadHistoricalProof(formData);
      setPayments(payments.map((payment, position) => (position === index
        ? { ...payment, proof_ids: [...payment.proof_ids, result.proof.id] }
        : payment)));
      notify('歷史付款圖片已上傳（上傳時間與付款日期分開保存）');
    } catch (failure) {
      setError(failure);
    }
  };

  return (
    <Modal
      title={mode === 'correct' ? '修正歷史補錄' : '新增歷史補錄'}
      subtitle={`帳簿啟用日 ${activationDate} · 所有日期必須早於啟用日`}
      onClose={onClose}
      wide
    >
      <ErrorBanner error={error} />
      <div className="grid form">
        <label className="field">
          <span>單位</span>
          <select value={form.unit_id} onChange={(event) => update('unit_id', event.target.value)} data-testid="historical-unit">
            {session.all_units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span>分類</span>
          <select value={form.category} onChange={(event) => update('category', event.target.value)} data-testid="historical-category">
            {session.categories.map((category) => <option key={category} value={category}>{category}</option>)}
          </select>
        </label>
        <label className="field" style={{ gridColumn: '1 / -1' }}>
          <span>項目名稱</span>
          <input value={form.title} onChange={(event) => update('title', event.target.value)} data-testid="historical-title" />
        </label>
        <label className="field"><span>帳務月份</span><input type="month" value={form.accounting_month} onChange={(event) => update('accounting_month', event.target.value)} data-testid="historical-month" /></label>
        <label className="field"><span>帳目日期</span><input type="date" value={form.item_date} onChange={(event) => update('item_date', event.target.value)} data-testid="historical-item-date" /></label>
        <label className="field"><span>原到期日</span><input type="date" value={form.due_date} onChange={(event) => update('due_date', event.target.value)} data-testid="historical-due-date" /></label>
        <label className="field"><span>總額（HKD）</span><input value={form.total} onChange={(event) => update('total', event.target.value)} data-testid="historical-total" /></label>
        <label className="field">
          <span>最終結果</span>
          <select value={form.outcome} onChange={(event) => update('outcome', event.target.value)} data-testid="historical-outcome">
            <option value="settled">已結清</option>
            <option value="cancelled">已取消</option>
          </select>
        </label>
        <label className="field">
          <span>收款人類型</span>
          <select value={form.recipient_kind} onChange={(event) => update('recipient_kind', event.target.value)} data-testid="historical-recipient-kind">
            <option value="external">群組以外</option>
            <option value="member">群組成員</option>
          </select>
        </label>
        {form.recipient_kind === 'member' ? (
          <label className="field">
            <span>收款成員</span>
            <select value={form.recipient_member_id} onChange={(event) => update('recipient_member_id', event.target.value)} data-testid="historical-recipient-member">
              {members.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}
            </select>
          </label>
        ) : (
          <label className="field"><span>外部收款人</span><input value={form.recipient_name} onChange={(event) => update('recipient_name', event.target.value)} data-testid="historical-recipient-name" /></label>
        )}
      </div>

      <fieldset style={{ marginTop: 14 }}>
        <legend>最終應付款（逐名成員）</legend>
        <div className="stack">
          {allocations.map((allocation, index) => (
            <div className="row" key={allocation.member_id} style={{ justifyContent: 'space-between' }}>
              <span>{nameOf(session.members, allocation.member_id)}</span>
              <span className="row">
                <input
                  style={{ maxWidth: 140 }}
                  value={allocation.amount}
                  data-testid={`historical-allocation-${nameOf(session.members, allocation.member_id)}`}
                  onChange={(event) => setAllocations(allocations.map((entry, position) => (
                    position === index ? { ...entry, amount: event.target.value } : entry
                  )))}
                />
                <span className="small muted">已補錄付款 {money(paidByMember.get(allocation.member_id) || 0)}</span>
              </span>
            </div>
          ))}
          <div className="spread">
            <span className="muted small">分攤合計 / 總額</span>
            <strong data-testid="historical-allocation-sum">{money(allocationSum)} / {money(totalCents || 0)}</strong>
          </div>
          {form.outcome === 'settled' ? (
            <p className="small muted">已結清項目必須逐名成員的歷史付款合計等於其最終應付款，不接受差額、超額或待處理結餘。</p>
          ) : null}
        </div>
      </fieldset>

      <fieldset style={{ marginTop: 14 }}>
        <legend>歷史付款</legend>
        <div className="stack">
          {payments.map((payment, index) => (
            <div className="card" key={index} style={{ boxShadow: 'none' }} data-testid={`historical-payment-${index}`}>
              <div className="grid form">
                <label className="field">
                  <span>付款人</span>
                  <select
                    value={payment.payer_member_id}
                    data-testid={`payment-payer-${index}`}
                    onChange={(event) => setPayments(payments.map((entry, position) => (
                      position === index ? { ...entry, payer_member_id: event.target.value } : entry
                    )))}
                  >
                    {members.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>金額（HKD）</span>
                  <input
                    value={payment.amount}
                    data-testid={`payment-amount-${index}`}
                    onChange={(event) => setPayments(payments.map((entry, position) => (
                      position === index ? { ...entry, amount: event.target.value } : entry
                    )))}
                  />
                </label>
                <label className="field">
                  <span>付款日期</span>
                  <input
                    type="date"
                    value={payment.paid_on}
                    data-testid={`payment-date-${index}`}
                    onChange={(event) => setPayments(payments.map((entry, position) => (
                      position === index ? { ...entry, paid_on: event.target.value } : entry
                    )))}
                  />
                </label>
                <label className="field">
                  <span>備註（選填）</span>
                  <input
                    value={payment.note}
                    data-testid={`payment-note-${index}`}
                    onChange={(event) => setPayments(payments.map((entry, position) => (
                      position === index ? { ...entry, note: event.target.value } : entry
                    )))}
                  />
                </label>
                <label className="field">
                  <span>付款圖片（可選、可多張）</span>
                  <input
                    type="file"
                    accept="image/*"
                    data-testid={`payment-image-${index}`}
                    onChange={(event) => uploadProof(index, event.target.files && event.target.files[0])}
                  />
                </label>
              </div>
              <div className="row small muted" style={{ marginTop: 6 }}>
                {payment.proof_ids.length === 0 ? '未附圖片' : payment.proof_ids.map((proofId) => (
                  <span className="chip" key={proofId}>
                    {proofId.slice(0, 12)}…
                    <button
                      type="button"
                      className="link"
                      data-testid={`payment-image-remove-${index}`}
                      onClick={() => setPayments(payments.map((entry, position) => (
                        position === index
                          ? { ...entry, proof_ids: entry.proof_ids.filter((candidate) => candidate !== proofId) }
                          : entry
                      )))}
                    >
                      移除
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className="link"
                  data-testid={`payment-remove-${index}`}
                  onClick={() => setPayments(payments.filter((unused, position) => position !== index))}
                >
                  刪除此筆歷史付款
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="small"
            data-testid="historical-add-payment"
            onClick={() => setPayments([...payments, {
              payer_member_id: members[0].id,
              amount: '',
              paid_on: beforeActivation,
              note: '',
              proof_ids: [],
            }])}
          >
            新增一筆歷史付款
          </button>
        </div>
      </fieldset>

      {form.outcome === 'cancelled' ? (
        <fieldset style={{ marginTop: 14 }}>
          <legend>取消資料及結餘處理</legend>
          <div className="grid form">
            <label className="field" style={{ gridColumn: '1 / -1' }}>
              <span>取消原因</span>
              <input value={form.cancel_reason} onChange={(event) => update('cancel_reason', event.target.value)} data-testid="historical-cancel-reason" />
            </label>
            <label className="field"><span>取消日期</span><input type="date" value={form.cancelled_on} onChange={(event) => update('cancelled_on', event.target.value)} data-testid="historical-cancel-date" /></label>
          </div>
          <div className="stack" style={{ marginTop: 10 }}>
            {dispositions.map((disposition, index) => (
              <div className="grid form" key={index} data-testid={`historical-disposition-${index}`}>
                <label className="field">
                  <span>成員</span>
                  <select
                    value={disposition.member_id}
                    data-testid={`disposition-member-${index}`}
                    onChange={(event) => setDispositions(dispositions.map((entry, position) => (
                      position === index ? { ...entry, member_id: event.target.value } : entry
                    )))}
                  >
                    {members.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>處理方式</span>
                  <select
                    value={disposition.kind}
                    data-testid={`disposition-kind-${index}`}
                    onChange={(event) => setDispositions(dispositions.map((entry, position) => (
                      position === index ? { ...entry, kind: event.target.value } : entry
                    )))}
                  >
                    <option value="returned">已退回</option>
                    <option value="transferred">已轉用</option>
                  </select>
                </label>
                <label className="field">
                  <span>金額（HKD）</span>
                  <input
                    value={disposition.amount}
                    data-testid={`disposition-amount-${index}`}
                    onChange={(event) => setDispositions(dispositions.map((entry, position) => (
                      position === index ? { ...entry, amount: event.target.value } : entry
                    )))}
                  />
                </label>
                <label className="field">
                  <span>處理日期</span>
                  <input
                    type="date"
                    value={disposition.handled_on}
                    data-testid={`disposition-date-${index}`}
                    onChange={(event) => setDispositions(dispositions.map((entry, position) => (
                      position === index ? { ...entry, handled_on: event.target.value } : entry
                    )))}
                  />
                </label>
              </div>
            ))}
            <button
              type="button"
              className="small"
              data-testid="historical-add-disposition"
              onClick={() => setDispositions([...dispositions, {
                member_id: members[0].id,
                kind: 'returned',
                amount: '',
                handled_on: beforeActivation,
                reason: '',
              }])}
            >
              新增結餘處理
            </button>
            <p className="small muted">取消前的每筆付款都必須記錄已退回或已轉用及處理日期，不能留下未處理的歷史款項。</p>
          </div>
        </fieldset>
      ) : null}

      {mode === 'correct' ? (
        <label className="field" style={{ marginTop: 14 }}>
          <span>修正原因（必填）</span>
          <textarea value={form.reason} onChange={(event) => update('reason', event.target.value)} data-testid="historical-reason" />
        </label>
      ) : null}

      {preview ? (
        <div className="card" style={{ marginTop: 14, boxShadow: 'none' }} data-testid="historical-summary">
          <h3>儲存前摘要</h3>
          <SummaryList rows={[
            ['項目名稱', preview.title],
            ['帳目日期', dateLabel(preview.item_date)],
            ['最終結果', preview.outcome === 'settled' ? '已結清' : '已取消'],
            ['總額', money(preview.total_cents)],
            ['歷史付款合計', money(preview.payment_total_cents)],
            ['歷史付款筆數', `${preview.payment_count} 筆`],
            ['圖片數量', `${preview.proof_count} 張`],
            ['帳簿啟用日', preview.activation_date],
          ]} />
        </div>
      ) : null}

      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button type="button" disabled={busy} onClick={runPreview} data-testid="historical-preview">檢視摘要</button>
        <button
          type="button"
          className="primary"
          disabled={busy || !preview || (mode === 'correct' && form.reason.trim().length < 2)}
          onClick={save}
          data-testid="historical-save"
        >
          {mode === 'correct' ? '確認並儲存修正' : '確認並儲存'}
        </button>
      </div>
    </Modal>
  );
}
